import { cn } from "@/lib/utils";
import {
	CustomFieldDefinition,
	CustomFieldEntity,
	CustomFieldPlacement,
	CustomFieldType,
} from "@/models/Organisation";
import { useOrganisation } from "@/networking/organisation.hooks";
import { customFieldPlacement, sortCustomFieldDefinitions } from "@/components/Shared/CustomFields";
import { SettingsLeaf } from "./SettingsLeafLayout";

/** Table grouping: jobs first, then customers, then consultants; ordered within each group. */
const ENTITY_ORDER: CustomFieldEntity[] = ["job", "customer", "consultant"];

/** Column "Kategori": which kind of record the field lives on. */
const ENTITY_LABEL: Record<CustomFieldEntity, string> = {
	job: "Uppdrag",
	customer: "Kund",
	consultant: "Konsult",
};

const TYPE_LABEL: Record<CustomFieldType, string> = {
	string: "Text",
	boolean: "Ja/Nej",
	number: "Nummer",
	select: "Lista",
	date: "Datum",
};

/** The host card a field is placed on, per entity (labels are the card titles). */
const PLACEMENT_LABEL: Record<CustomFieldEntity, Partial<Record<CustomFieldPlacement, string>>> = {
	customer: {
		profil: "Grunduppgifter",
		ovrigt: "Övriga uppgifter",
		faktura: "Fakturamottagare",
		"faktura-ovrigt": "Övriga faktureringsuppgifter",
	},
	job: {
		bokning: "Bokning & referenser",
		ovrigt: "Övriga uppgifter",
	},
	consultant: {
		grunduppgifter: "Grunduppgifter",
		anstallning: "Anställning",
		ovrigt: "Övriga uppgifter",
	},
};

const placementLabel = (item: CustomFieldDefinition) => {
	const placement = customFieldPlacement(item);
	return PLACEMENT_LABEL[item.entity][placement] ?? placement;
};

const formatDefault = (item: CustomFieldDefinition) => {
	if (item.defaultValue === undefined || item.defaultValue === "") return null;
	if (item.type === "boolean") return item.defaultValue ? "Ja" : "Nej";
	if (item.type === "select") {
		return (
			item.options?.find((option) => option.key === item.defaultValue)?.label.sv ??
			String(item.defaultValue)
		);
	}
	return String(item.defaultValue);
};

/** "Ärvs till uppdrag" cell: customer fields only. */
const inheritanceText = (item: CustomFieldDefinition) => {
	if (item.entity !== "customer" || !item.inheritance || item.inheritance === "none") return null;
	return {
		text: item.inheritance === "override" ? "Ja" : "Visas (skrivskyddat)",
		where: item.jobPlacement === "bokning" ? "Bokning & referenser" : "Övriga uppgifter",
	};
};

const headerClasses =
	"px-2 py-1.5 text-left text-[11px] font-medium uppercase tracking-wide text-gray-400";
const cellClasses = "px-2 py-2.5 align-top text-sm";
const mutedCell = cn(cellClasses, "text-gray-700");
const empty = <span className="text-gray-300">–</span>;

/**
 * "Anpassade fält" — the org's active `customFieldDefinitions` in one
 * read-only table across all entities. Fields are created, edited, reordered
 * and archived by Origo super admins in oo-admin; here the organisation sees
 * which fields exist, where they render and how customer fields are inherited.
 */
const AnpassadeFaltPage = () => {
	const { data: organisation } = useOrganisation();

	const definitions = (organisation?.customFieldDefinitions ?? []).filter(
		(definition) => !definition.archived
	);
	const items = ENTITY_ORDER.flatMap((entity) =>
		sortCustomFieldDefinitions(definitions.filter((definition) => definition.entity === entity))
	);

	return (
		<SettingsLeaf
			wide
			title="Anpassade fält"
			description="Egna fält på uppdrag, kunder och konsulter. Varje fält visas i kortet under Visas under, i den här ordningen, och kundfält kan ärvas till kundens uppdrag. Värdena följer med till faktura- och lönekörningar och rapporter. Kontakta Origo för att lägga till eller ändra ett fält."
		>
			{items.length === 0 ? (
				<p className="text-sm text-gray-500">Inga anpassade fält ännu.</p>
			) : (
				<table className="w-full border-collapse">
					<thead>
						<tr className="border-b border-gray-200">
							<th className={headerClasses}>Namn</th>
							<th className={cn(headerClasses, "w-24 whitespace-nowrap")}>Kategori</th>
							<th className={cn(headerClasses, "w-28 whitespace-nowrap")}>Typ</th>
							<th className={cn(headerClasses, "w-48 whitespace-nowrap")}>Visas under</th>
							<th className={cn(headerClasses, "w-40 whitespace-nowrap")}>Ärvs till uppdrag</th>
							<th className={cn(headerClasses, "w-28 whitespace-nowrap")}>Standard</th>
						</tr>
					</thead>
					<tbody>
						{items.map((item) => {
							const inheritance = inheritanceText(item);
							const defaultValue = formatDefault(item);
							return (
								<tr key={item.key} className="border-t border-gray-200">
									<td className={cn(cellClasses, "min-w-0")}>
										<div className="flex flex-col gap-0.5">
											<div className="flex flex-wrap items-center gap-x-2 gap-y-1">
												<span className="font-medium text-gray-800">{item.label.sv}</span>
												{item.entity === "customer" && item.groupOwned && (
													<span className="inline-flex items-center rounded-full border border-blue-200 bg-blue-50 px-2 py-0.5 text-[11px] font-medium text-blue-800">
														Ägs av koncern
													</span>
												)}
												{item.required && (
													<span className="text-xs text-gray-500">Obligatoriskt</span>
												)}
											</div>
											{item.description?.sv && (
												<span className="text-xs text-gray-500">{item.description.sv}</span>
											)}
										</div>
									</td>
									<td className={mutedCell}>{ENTITY_LABEL[item.entity]}</td>
									<td className={mutedCell}>
										{item.type === "select"
											? `Lista (${item.options?.length ?? 0})`
											: TYPE_LABEL[item.type] ?? item.type}
									</td>
									<td className={mutedCell}>{placementLabel(item)}</td>
									<td className={mutedCell}>
										{inheritance ? (
											<div className="flex flex-col">
												<span>{inheritance.text}</span>
												<span className="text-xs text-gray-500">{inheritance.where}</span>
											</div>
										) : (
											empty
										)}
									</td>
									<td className={mutedCell}>{defaultValue ?? empty}</td>
								</tr>
							);
						})}
					</tbody>
				</table>
			)}
		</SettingsLeaf>
	);
};

export default AnpassadeFaltPage;
